import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Mail, ArrowRight, CheckCircle2, 
    Sparkles, AlertCircle, Building2, User, HelpCircle
} from 'lucide-react';
import { LandingNav } from '../components/LandingNav';
import { LandingFooter } from '../components/LandingFooter';
import apiClient from '@/core/api/client';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import './landingPage.css';
import './staticPage.css';
import './contactPage.css';

interface FormState {
    name: string;
    email: string;
    school_name: string;
    subject: string;
    message: string;
}

interface ContactResponse {
    success: boolean;
    data?: {
        id: string;
        name: string;
        email: string;
        school_name: string;
        subject: string;
        message: string;
        created_at: string;
    };
    message?: string;
    error?: string;
}

const INITIAL_FORM_STATE: FormState = {
    name: '',
    email: '',
    school_name: '',
    subject: '',
    message: '',
};

export const ContactPage: React.FC = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<FormState>(INITIAL_FORM_STATE);
    const [errors, setErrors] = useState<Partial<FormState>>({});
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name as keyof FormState]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<FormState> = {};
        
        if (!formData.name.trim()) newErrors.name = 'Full name is required';
        
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        
        if (!formData.school_name.trim()) newErrors.school_name = 'School name is required';
        
        if (!formData.subject) newErrors.subject = 'Please select a subject';
        
        if (!formData.message.trim()) {
            newErrors.message = 'Please tell us about your requirements';
        } else if (formData.message.trim().length < 10) {
            newErrors.message = 'Message must be at least 10 characters long';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);
        if (!validateForm()) return;

        setIsSubmitting(true);
        
        try {
            const response = await apiClient.post<ContactResponse>(API_ENDPOINTS.CONTACT, formData);
            if (response.data.success) {
                setIsSuccess(true);
                setFormData(INITIAL_FORM_STATE);
            } else {
                setSubmitError(response.data.error || response.data.message || 'Failed to submit inquiry. Please try again.');
            }
        } catch (error: any) {
            console.error('Contact submission failed:', error);
            const errMsg = error.response?.data?.error || error.response?.data?.message || 'An error occurred while sending your request. Please check your network connection and try again.';
            setSubmitError(errMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="raabta-landing">
            <LandingNav showMenu={true} />

            {/* ── Main Contact Layout ── */}
            <section className="contact-section">
                {/* <div className="contact-bg-glows">
                    <div className="contact-glow contact-glow--1" />
                    <div className="contact-glow contact-glow--2" />
                </div> */}

                <div className="raabta-container">
                    <div className="contact-header reveal is-visible">
                        <h1 className="contact-title">Contact Us</h1>
                        <p className="contact-subtitle">
                            Have a question, feedback, or need assistance? Fill out the form below and our team will get back to you shortly.
                        </p>
                    </div>

                    <div className="contact-form-wrapper-centered reveal is-visible">
                        <div className="contact-form-container">
                            {!isSuccess ? (
                                <form className="contact-form" onSubmit={handleSubmit}>
                                    {submitError && (
                                        <div style={{
                                            padding: '1rem',
                                            borderRadius: '12px',
                                            backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                            color: '#ef4444',
                                            border: '1px solid rgba(239, 68, 68, 0.2)',
                                            fontSize: '0.875rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            fontWeight: 500
                                        }}>
                                            <AlertCircle size={16} />
                                            <span>{submitError}</span>
                                        </div>
                                    )}

                                    <div className="form-double-col">
                                        <div className="form-group-item">
                                            <label htmlFor="name">Full Name *</label>
                                            <div className="input-with-icon">
                                                <User className="input-icon" size={16} />
                                                <input 
                                                    type="text" 
                                                    id="name" 
                                                    name="name" 
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    placeholder="Enter your name " 
                                                    className={errors.name ? 'input-error' : ''}
                                                />
                                            </div>
                                            {errors.name && <span className="error-message"><AlertCircle size={12} /> {errors.name}</span>}
                                        </div>

                                        <div className="form-group-item">
                                            <label htmlFor="email">Work Email *</label>
                                            <div className="input-with-icon">
                                                <Mail className="input-icon" size={16} />
                                                <input 
                                                    type="email" 
                                                    id="email" 
                                                    name="email" 
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    placeholder="Enter your email" 
                                                    className={errors.email ? 'input-error' : ''}
                                                />
                                            </div>
                                            {errors.email && <span className="error-message"><AlertCircle size={12} /> {errors.email}</span>}
                                        </div>
                                    </div>

                                    <div className="form-double-col">
                                        <div className="form-group-item">
                                            <label htmlFor="school_name">School / Organization *</label>
                                            <div className="input-with-icon">
                                                <Building2 className="input-icon" size={16} />
                                                <input 
                                                    type="text" 
                                                    id="school_name" 
                                                    name="school_name" 
                                                    value={formData.school_name}
                                                    onChange={handleChange}
                                                    placeholder="Enter your school name if any " 
                                                    className={errors.school_name ? 'input-error' : ''}
                                                />
                                            </div>
                                            {errors.school_name && <span className="error-message"><AlertCircle size={12} /> {errors.school_name}</span>}
                                        </div>

                                        <div className="form-group-item">
                                            <label htmlFor="subject">Subject *</label>
                                            <div className="input-with-icon">
                                                <HelpCircle className="input-icon" size={16} />
                                                <select 
                                                    id="subject" 
                                                    name="subject" 
                                                    value={formData.subject}
                                                    onChange={handleChange}
                                                    className={errors.subject ? 'input-error' : ''}
                                                >
                                                    <option value="">Select subject...</option>
                                                    <option value="Demo Request">Request a Demo</option>
                                                    <option value="Pricing Inquiry">Pricing Questions</option>
                                                    <option value="General Support">General Support</option>
                                                    <option value="Partnership Request">Partnership Request</option>
                                                    <option value="Other">Other</option>
                                                </select>
                                            </div>
                                            {errors.subject && <span className="error-message"><AlertCircle size={12} /> {errors.subject}</span>}
                                        </div>
                                    </div>

                                    <div className="form-group-item">
                                        <label htmlFor="message">How can we help you? *</label>
                                        <textarea 
                                            id="message" 
                                            name="message" 
                                            rows={4}
                                            value={formData.message}
                                            onChange={handleChange}
                                            placeholder="Write your message here..."
                                            className={errors.message ? 'input-error' : ''}
                                        ></textarea>
                                        {errors.message && <span className="error-message"><AlertCircle size={12} /> {errors.message}</span>}
                                    </div>

                                    <button 
                                        type="submit" 
                                        className="raabta-btn raabta-btn--primary raabta-btn--lg contact-submit-btn"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Sending Request...' : 'Submit Inquiry'}
                                        {!isSubmitting && <ArrowRight size={16} />}
                                    </button>
                                </form>
                            ) : (
                                <div className="contact-success-state">
                                    <div className="success-icon-wrap">
                                        <CheckCircle2 size={56} className="success-checkmark" />
                                    </div>
                                    <h2>Inquiry Sent Successfully!</h2>
                                    <p>
                                        Thank you for reaching out to Raabta. We have received your inquiry.
                                    </p>
                                    <div className="success-actions">
                                        <button 
                                            className="raabta-btn raabta-btn--primary"
                                            onClick={() => navigate('/')}
                                        >
                                            Back to Homepage
                                        </button>
                                        <button 
                                            className="raabta-btn raabta-btn--ghost"
                                            onClick={() => setIsSuccess(false)}
                                        >
                                            Send another message
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <LandingFooter />
        </div>
    );
};
